import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { 
  auth, 
  onAuthStateChanged, 
  User, 
  db, 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  onSnapshot, 
  setDoc, 
  deleteDoc, 
  serverTimestamp,
  handleFirestoreError,
  OperationType 
} from './firebase';

import { StateVendorCredential } from './types/stateCredentials';

export interface VendorProfile {
  id?: string;
  uid: string;
  companyName: string;
  label?: string; // Optional identifier or nickname e.g. "Primary HQ", "Federal Systems Division", "DBA Subsidiary"
  isDefault?: boolean;
  ein?: string;
  duns?: string;
  dunsNumber?: string;
  companyId?: string;
  dosId?: string;
  incorporationDate?: string;
  cageCode?: string;
  samUei?: string;
  address?: string;
  contactEmail?: string;
  website?: string;
  linkedinUrl?: string;
  partnerOnUrl?: string;
  businessRegisteredCountry?: string;
  businessRegisteredState?: string;
  capabilities?: string[];
  designations?: string[];
  naicsCodes?: string[];
  nigpCodes?: string[];
  accountNumber?: string;
  bankName?: string;
  routingNumber?: string;
  taxClassification?: string;
  paymentTerms?: string;
  subscriptionTier?: 'starter' | 'pro' | 'enterprise';
  paymentStatus?: string;
  
  // State Registrations & Vendor Credentials Map
  stateRegistrations?: Record<string, StateVendorCredential>;

  // Contacts
  primaryContactFirstname?: string;
  primaryContactLastname?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  primaryContactTitle?: string;
  businessContactFirstname?: string;
  businessContactLastname?: string;
  businessContactEmail?: string;
  businessContactPhone?: string;
  businessContactTitle?: string;

  // Structured Addresses
  businessAddress?: any;
  shippingAddress?: any;
  billToAddress?: any;
  invoicingAddress?: any;
  remittanceAddress?: any;

  // Flat address fields for backward compatibility
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;

  // Intelligence
  websiteIntelligence?: any;
  linkedInIntelligence?: any;
  partnerOnIntelligence?: any;
  businessEntityData?: any;

  // Branding
  logoUrl?: string;
  colorScheme?: string;
  mainColor?: string;
  bgColor?: string;
  fontFamily?: string;
  themeMode?: string;

  createdAt?: any;
  updatedAt?: any;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  vendorProfile: VendorProfile | null;
  vendorProfiles: VendorProfile[];
  activeProfileId: string | null;
  loading: boolean;
  isAuthReady: boolean;
  switchProfile: (profileId: string) => Promise<void>;
  createProfile: (profileData: Partial<VendorProfile>, setAsActive?: boolean) => Promise<string>;
  updateCurrentProfile: (profileData: Partial<VendorProfile>, targetProfileId?: string) => Promise<void>;
  updateStateCredential: (stateCode: string, credentialData: Partial<StateVendorCredential>, targetProfileId?: string) => Promise<void>;
  batchUpdateStateCredentials: (credentialsMap: Record<string, Partial<StateVendorCredential>>, targetProfileId?: string) => Promise<void>;
  duplicateProfile: (profileId: string, customCompanyName?: string) => Promise<string>;
  deleteProfile: (profileId: string) => Promise<void>;
  setDefaultProfile: (profileId: string) => Promise<void>;
  deleteBusinessEntityRegistryData: (targetProfileId?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  vendorProfile: null,
  vendorProfiles: [],
  activeProfileId: null,
  loading: true,
  isAuthReady: false,
  switchProfile: async () => {},
  createProfile: async () => '',
  updateCurrentProfile: async () => {},
  updateStateCredential: async () => {},
  batchUpdateStateCredentials: async () => {},
  duplicateProfile: async () => '',
  deleteProfile: async () => {},
  setDefaultProfile: async () => {},
  deleteBusinessEntityRegistryData: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [vendorProfiles, setVendorProfiles] = useState<VendorProfile[]>([]);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const isMigratingRef = useRef(false);

  // Derive active vendorProfile from list or fallback
  const vendorProfile = vendorProfiles.find(p => p.id === activeProfileId) || vendorProfiles[0] || null;

  // Listen to Auth State
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsAuthReady(true);
      if (!currentUser) {
        setVendorProfiles([]);
        setActiveProfileId(null);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // Listen to Multi-Vendor Profiles collection
  useEffect(() => {
    if (!user) return;

    setLoading(true);
    const profilesQuery = query(
      collection(db, 'vendor_profiles'),
      where('uid', '==', user.uid)
    );

    const unsubscribeProfiles = onSnapshot(profilesQuery, async (snapshot) => {
      const loadedProfiles: VendorProfile[] = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...(docSnap.data() as any)
      }));

      // If user has no vendor_profiles documents yet, check for legacy /vendors/{uid} document
      if (loadedProfiles.length === 0 && !isMigratingRef.current) {
        isMigratingRef.current = true;
        try {
          const legacyDocSnap = await getDoc(doc(db, 'vendors', user.uid));
          if (legacyDocSnap.exists()) {
            const legacyData = legacyDocSnap.data() as VendorProfile;
            const newProfileData: VendorProfile = {
              ...legacyData,
              id: user.uid,
              uid: user.uid,
              companyName: legacyData.companyName || user.displayName || 'My Company Inc.',
              label: legacyData.label || 'Primary Entity',
              isDefault: true,
              createdAt: legacyData.createdAt || serverTimestamp(),
              updatedAt: serverTimestamp()
            };
            
            // Save to vendor_profiles collection for seamless multi-profile transition
            await setDoc(doc(db, 'vendor_profiles', user.uid), newProfileData);
            setVendorProfiles([newProfileData]);
            setActiveProfileId(user.uid);
            localStorage.setItem(`rfx_active_profile_id_${user.uid}`, user.uid);
            setLoading(false);
            isMigratingRef.current = false;
            return;
          } else {
            // First time brand new user: create initial default profile
            const initialId = user.uid;
            const derivedCompanyName = user.email?.includes('quantumai') 
              ? 'QUANTUM AI INC' 
              : (user.displayName ? `${user.displayName} Enterprise` : 'QUANTUM AI INC');

            const isQuantumAI = derivedCompanyName.toUpperCase().includes('QUANTUM AI');

            const initialProfile: VendorProfile = {
              id: initialId,
              uid: user.uid,
              companyName: derivedCompanyName,
              label: 'Primary Entity',
              companyId: isQuantumAI ? 'DB288956' : 'DB288956',
              dosId: isQuantumAI ? 'DB288956' : 'DB288956',
              incorporationDate: isQuantumAI ? '2024-12-13' : '2024-12-13',
              dunsNumber: '08-492-1845',
              contactEmail: user.email || 'isa.hunter@quantumaiinc.tech',
              address: '100 Innovation Way, Suite 400',
              businessRegisteredState: 'South Dakota',
              businessRegisteredCountry: 'United States',
              taxClassification: 'Corporation',
              capabilities: ['(SB) Small Business', 'Enterprise Artificial Intelligence', 'Defense & Public Sector Analytics'],
              designations: ['(SB) Small Business'],
              naicsCodes: ['541511', '541512', '541715'],
              nigpCodes: ['920-00', '918-29'],
              isDefault: true,
              subscriptionTier: 'starter',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };

            await setDoc(doc(db, 'vendor_profiles', initialId), initialProfile);
            await setDoc(doc(db, 'vendors', user.uid), initialProfile, { merge: true });
            setVendorProfiles([initialProfile]);
            setActiveProfileId(initialId);
            localStorage.setItem(`rfx_active_profile_id_${user.uid}`, initialId);
            setLoading(false);
            isMigratingRef.current = false;
            return;
          }
        } catch (err) {
          console.error("Error migrating or creating initial vendor profile:", err);
        } finally {
          isMigratingRef.current = false;
        }
      }

      setVendorProfiles(loadedProfiles);

      // Determine active profile
      const savedActiveId = localStorage.getItem(`rfx_active_profile_id_${user.uid}`);
      if (savedActiveId && loadedProfiles.some(p => p.id === savedActiveId)) {
        setActiveProfileId(savedActiveId);
      } else if (loadedProfiles.length > 0) {
        const defaultProfile = loadedProfiles.find(p => p.isDefault) || loadedProfiles[0];
        setActiveProfileId(defaultProfile.id || null);
        if (defaultProfile.id) {
          localStorage.setItem(`rfx_active_profile_id_${user.uid}`, defaultProfile.id);
        }
      } else {
        setActiveProfileId(null);
      }

      setLoading(false);
    }, (error) => {
      console.error("Error fetching vendor profiles:", error);
      handleFirestoreError(error, OperationType.LIST, 'vendor_profiles');
      setLoading(false);
    });

    return () => unsubscribeProfiles();
  }, [user]);

  // Switch Active Profile
  const switchProfile = useCallback(async (profileId: string) => {
    if (!user) return;
    const target = vendorProfiles.find(p => p.id === profileId);
    if (!target) return;

    setActiveProfileId(profileId);
    localStorage.setItem(`rfx_active_profile_id_${user.uid}`, profileId);

    // Sync active profile to legacy /vendors/{uid} path for backward compatibility
    try {
      await setDoc(doc(db, 'vendors', user.uid), {
        ...target,
        uid: user.uid,
        activeProfileId: profileId,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (err) {
      console.warn("Could not sync active profile to /vendors/{uid}:", err);
    }
  }, [user, vendorProfiles]);

  // Create a new vendor profile
  const createProfile = useCallback(async (profileData: Partial<VendorProfile>, setAsActive = true): Promise<string> => {
    if (!user) throw new Error("User must be authenticated to create a profile");

    const newDocRef = doc(collection(db, 'vendor_profiles'));
    const newId = newDocRef.id;

    const newProfile: VendorProfile = {
      ...profileData,
      id: newId,
      uid: user.uid,
      companyName: profileData.companyName?.trim() || 'New Vendor Profile',
      label: profileData.label?.trim() || (vendorProfiles.length === 0 ? 'Primary Entity' : `Profile #${vendorProfiles.length + 1}`),
      isDefault: vendorProfiles.length === 0 || profileData.isDefault === true,
      subscriptionTier: profileData.subscriptionTier || vendorProfile?.subscriptionTier || 'starter',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(newDocRef, newProfile);

    if (setAsActive || vendorProfiles.length === 0) {
      setActiveProfileId(newId);
      localStorage.setItem(`rfx_active_profile_id_${user.uid}`, newId);
      try {
        await setDoc(doc(db, 'vendors', user.uid), {
          ...newProfile,
          uid: user.uid,
          activeProfileId: newId,
          updatedAt: serverTimestamp()
        }, { merge: true });
      } catch (err) {
        console.warn("Syncing new profile to /vendors/{uid} failed:", err);
      }
    }

    return newId;
  }, [user, vendorProfiles, vendorProfile]);

  // Update current or target vendor profile
  const updateCurrentProfile = useCallback(async (profileData: Partial<VendorProfile>, targetProfileId?: string) => {
    if (!user) throw new Error("User must be authenticated to update profile");
    const targetId = targetProfileId || activeProfileId || user.uid;

    const cleanData = {
      ...profileData,
      uid: user.uid,
      updatedAt: serverTimestamp()
    };

    // Update in vendor_profiles collection
    const targetDocRef = doc(db, 'vendor_profiles', targetId);
    await setDoc(targetDocRef, cleanData, { merge: true });

    // If updating the active profile, also sync to /vendors/{uid}
    if (targetId === activeProfileId || !activeProfileId) {
      try {
        await setDoc(doc(db, 'vendors', user.uid), {
          ...cleanData,
          uid: user.uid,
          activeProfileId: targetId
        }, { merge: true });
      } catch (err) {
        console.warn("Syncing update to /vendors/{uid} failed:", err);
      }
    }
  }, [user, activeProfileId]);

  // Update specific state credential for active or targeted profile
  const updateStateCredential = useCallback(async (
    stateCode: string, 
    credentialData: Partial<StateVendorCredential>, 
    targetProfileId?: string
  ) => {
    if (!user) throw new Error("User must be authenticated to update state credentials");
    const code = stateCode.toUpperCase();
    const targetId = targetProfileId || activeProfileId || user.uid;
    const targetProfile = vendorProfiles.find(p => p.id === targetId) || vendorProfile;
    const entityCompanyName = targetProfile?.companyName || 'Active Entity';

    const currentRegistrations = (targetProfile?.stateRegistrations || {}) as Record<string, StateVendorCredential>;
    const existingCred: Partial<StateVendorCredential> = currentRegistrations[code] || {};

    const updatedCred: StateVendorCredential = {
      stateCode: code,
      stateName: credentialData.stateName || existingCred.stateName || code,
      isRegistered: credentialData.isRegistered !== undefined ? credentialData.isRegistered : (existingCred.isRegistered ?? false),
      registrationStatus: credentialData.registrationStatus || (credentialData.isRegistered ? 'active' : (existingCred.registrationStatus || 'not_registered')),
      vendorId: credentialData.vendorId !== undefined ? credentialData.vendorId : (existingCred.vendorId || ''),
      username: credentialData.username !== undefined ? credentialData.username : (existingCred.username || ''),
      password: credentialData.password !== undefined ? credentialData.password : (existingCred.password || ''),
      stateTaxId: credentialData.stateTaxId !== undefined ? credentialData.stateTaxId : (existingCred.stateTaxId || ''),
      stateLicenseNumber: credentialData.stateLicenseNumber !== undefined ? credentialData.stateLicenseNumber : (existingCred.stateLicenseNumber || ''),
      dbeCertificationNumber: credentialData.dbeCertificationNumber !== undefined ? credentialData.dbeCertificationNumber : (existingCred.dbeCertificationNumber || ''),
      dbeExpirationDate: credentialData.dbeExpirationDate !== undefined ? credentialData.dbeExpirationDate : (existingCred.dbeExpirationDate || ''),
      contactName: credentialData.contactName !== undefined ? credentialData.contactName : (existingCred.contactName || ''),
      contactEmail: credentialData.contactEmail !== undefined ? credentialData.contactEmail : (existingCred.contactEmail || ''),
      contactPhone: credentialData.contactPhone !== undefined ? credentialData.contactPhone : (existingCred.contactPhone || ''),
      customLoginUrl: credentialData.customLoginUrl !== undefined ? credentialData.customLoginUrl : (existingCred.customLoginUrl || ''),
      portalUrl: credentialData.portalUrl !== undefined ? credentialData.portalUrl : (existingCred.portalUrl || ''),
      notes: credentialData.notes !== undefined ? credentialData.notes : (existingCred.notes || ''),
      updatedAt: new Date().toISOString()
    };

    const newRegistrations = {
      ...currentRegistrations,
      [code]: updatedCred
    };

    // 1. Update in vendor_profiles Firestore doc
    const targetDocRef = doc(db, 'vendor_profiles', targetId);
    await setDoc(targetDocRef, {
      stateRegistrations: newRegistrations,
      updatedAt: serverTimestamp()
    }, { merge: true });

    // 2. Also update in /vendors/{uid} if active profile
    if (targetId === activeProfileId || !activeProfileId) {
      try {
        await setDoc(doc(db, 'vendors', user.uid), {
          stateRegistrations: newRegistrations,
          updatedAt: serverTimestamp()
        }, { merge: true });
      } catch (e) {
        console.warn("Syncing state registration to /vendors failed:", e);
      }
    }

    // 3. Sync to state_profiles/{code} collection with entity sub-map
    try {
      const stateDocRef = doc(db, 'state_profiles', code);
      await setDoc(stateDocRef, {
        state: updatedCred.stateName,
        stateCode: code,
        updatedAt: serverTimestamp(),
        [`entities.${targetId}`]: {
          entityId: targetId,
          entityName: entityCompanyName,
          vendorId: updatedCred.vendorId || '',
          username: updatedCred.username || '',
          password: updatedCred.password || '',
          isRegistered: updatedCred.isRegistered,
          registrationStatus: updatedCred.registrationStatus,
          stateTaxId: updatedCred.stateTaxId || '',
          stateLicenseNumber: updatedCred.stateLicenseNumber || '',
          dbeCertificationNumber: updatedCred.dbeCertificationNumber || '',
          updatedAt: serverTimestamp()
        },
        // Root fallbacks for backward compatibility
        vendorId: updatedCred.vendorId || '',
        username: updatedCred.username || '',
        password: updatedCred.password || '',
        isRegistered: updatedCred.isRegistered
      }, { merge: true });
    } catch (err) {
      console.warn(`Syncing credential to state_profiles/${code} notice:`, err);
    }
  }, [user, activeProfileId, vendorProfiles, vendorProfile]);

  // Batch update credentials for multiple states
  const batchUpdateStateCredentials = useCallback(async (
    credentialsMap: Record<string, Partial<StateVendorCredential>>,
    targetProfileId?: string
  ) => {
    if (!user) throw new Error("User must be authenticated to batch update credentials");
    const targetId = targetProfileId || activeProfileId || user.uid;
    const targetProfile = vendorProfiles.find(p => p.id === targetId) || vendorProfile;
    const currentRegistrations = (targetProfile?.stateRegistrations || {}) as Record<string, StateVendorCredential>;

    const updatedRegistrations: Record<string, StateVendorCredential> = { ...currentRegistrations };

    for (const [code, credData] of Object.entries(credentialsMap)) {
      const upperCode = code.toUpperCase();
      const existing: Partial<StateVendorCredential> = currentRegistrations[upperCode] || {};
      updatedRegistrations[upperCode] = {
        stateCode: upperCode,
        stateName: credData.stateName || existing.stateName || upperCode,
        isRegistered: credData.isRegistered !== undefined ? credData.isRegistered : (existing.isRegistered ?? false),
        registrationStatus: credData.registrationStatus || (credData.isRegistered ? 'active' : (existing.registrationStatus || 'not_registered')),
        vendorId: credData.vendorId !== undefined ? credData.vendorId : (existing.vendorId || ''),
        username: credData.username !== undefined ? credData.username : (existing.username || ''),
        password: credData.password !== undefined ? credData.password : (existing.password || ''),
        stateTaxId: credData.stateTaxId !== undefined ? credData.stateTaxId : (existing.stateTaxId || ''),
        stateLicenseNumber: credData.stateLicenseNumber !== undefined ? credData.stateLicenseNumber : (existing.stateLicenseNumber || ''),
        dbeCertificationNumber: credData.dbeCertificationNumber !== undefined ? credData.dbeCertificationNumber : (existing.dbeCertificationNumber || ''),
        dbeExpirationDate: credData.dbeExpirationDate !== undefined ? credData.dbeExpirationDate : (existing.dbeExpirationDate || ''),
        contactName: credData.contactName !== undefined ? credData.contactName : (existing.contactName || ''),
        contactEmail: credData.contactEmail !== undefined ? credData.contactEmail : (existing.contactEmail || ''),
        contactPhone: credData.contactPhone !== undefined ? credData.contactPhone : (existing.contactPhone || ''),
        customLoginUrl: credData.customLoginUrl !== undefined ? credData.customLoginUrl : (existing.customLoginUrl || ''),
        portalUrl: credData.portalUrl !== undefined ? credData.portalUrl : (existing.portalUrl || ''),
        notes: credData.notes !== undefined ? credData.notes : (existing.notes || ''),
        updatedAt: new Date().toISOString()
      };
    }

    const targetDocRef = doc(db, 'vendor_profiles', targetId);
    await setDoc(targetDocRef, {
      stateRegistrations: updatedRegistrations,
      updatedAt: serverTimestamp()
    }, { merge: true });

    if (targetId === activeProfileId || !activeProfileId) {
      try {
        await setDoc(doc(db, 'vendors', user.uid), {
          stateRegistrations: updatedRegistrations,
          updatedAt: serverTimestamp()
        }, { merge: true });
      } catch (e) {
        console.warn("Syncing batch state registrations to /vendors failed:", e);
      }
    }
  }, [user, activeProfileId, vendorProfiles, vendorProfile]);

  // Duplicate an existing profile (e.g. for DBA, subsidiary, joint venture)
  const duplicateProfile = useCallback(async (profileId: string, customCompanyName?: string): Promise<string> => {
    if (!user) throw new Error("User must be authenticated to duplicate a profile");
    const sourceProfile = vendorProfiles.find(p => p.id === profileId);
    if (!sourceProfile) throw new Error("Source profile not found");

    const newDocRef = doc(collection(db, 'vendor_profiles'));
    const newId = newDocRef.id;

    const newCompanyName = customCompanyName?.trim() || `${sourceProfile.companyName} (DBA / Division)`;
    const newLabel = `${sourceProfile.label || 'Entity'} (Copy)`;

    const duplicatedData: VendorProfile = {
      ...sourceProfile,
      id: newId,
      uid: user.uid,
      companyName: newCompanyName,
      label: newLabel,
      isDefault: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(newDocRef, duplicatedData);
    await switchProfile(newId);
    return newId;
  }, [user, vendorProfiles, switchProfile]);

  // Delete a vendor profile (business entity)
  const deleteProfile = useCallback(async (profileId: string) => {
    if (!user) throw new Error("User must be authenticated to delete a profile");

    // Case 1: User is deleting their only profile - reset and create a clean entity
    if (vendorProfiles.length <= 1) {
      const freshDocRef = doc(collection(db, 'vendor_profiles'));
      const freshId = freshDocRef.id;
      const freshProfile: VendorProfile = {
        id: freshId,
        uid: user.uid,
        companyName: 'New Vendor Entity',
        label: 'Primary Entity',
        isDefault: true,
        subscriptionTier: 'starter',
        capabilities: [],
        designations: [],
        naicsCodes: [],
        nigpCodes: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(freshDocRef, freshProfile);
      await deleteDoc(doc(db, 'vendor_profiles', profileId));
      await switchProfile(freshId);
      return;
    }

    // Case 2: Multiple profiles exist
    await deleteDoc(doc(db, 'vendor_profiles', profileId));

    // If we deleted the active profile, switch to the first remaining profile
    if (profileId === activeProfileId) {
      const remaining = vendorProfiles.filter(p => p.id !== profileId);
      if (remaining.length > 0) {
        const nextActive = remaining.find(p => p.isDefault) || remaining[0];
        await switchProfile(nextActive.id!);
      }
    }
  }, [user, vendorProfiles, activeProfileId, switchProfile]);

  // Set default profile
  const setDefaultProfile = useCallback(async (profileId: string) => {
    if (!user) return;
    for (const p of vendorProfiles) {
      if (p.id) {
        await setDoc(doc(db, 'vendor_profiles', p.id), {
          isDefault: p.id === profileId,
          updatedAt: serverTimestamp()
        }, { merge: true });
      }
    }
  }, [user, vendorProfiles]);

  // Delete / Detach corporate business entity registry records from a profile
  const deleteBusinessEntityRegistryData = useCallback(async (targetProfileId?: string) => {
    if (!user) throw new Error("User must be authenticated to modify entity registry data");
    const targetId = targetProfileId || activeProfileId || user.uid;

    const clearPayload = {
      businessEntityData: null,
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, 'vendor_profiles', targetId), clearPayload, { merge: true });

    if (targetId === activeProfileId || !activeProfileId) {
      try {
        await setDoc(doc(db, 'vendors', user.uid), clearPayload, { merge: true });
      } catch (err) {
        console.warn("Syncing entity registry clearance to /vendors failed:", err);
      }
    }
  }, [user, activeProfileId]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      vendorProfile, 
      vendorProfiles, 
      activeProfileId, 
      loading, 
      isAuthReady,
      switchProfile,
      createProfile,
      updateCurrentProfile,
      updateStateCredential,
      batchUpdateStateCredentials,
      duplicateProfile,
      deleteProfile,
      setDefaultProfile,
      deleteBusinessEntityRegistryData
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
